import '@fontsource-variable/fraunces';
import '@fontsource-variable/manrope';
import '@fontsource-variable/noto-sans-tamil';
import '@fontsource-variable/noto-serif-tamil';
import { createRootRoute, HeadContent, Outlet, Scripts } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react';
import appCss from '../index.css?url';

import { AnimatedLayout } from '@components/AnimatedLayout';
import { NotFoundPage } from '@components/errors/NotFoundPage';
import { MenuProvider } from '@components/layout/Header/Menu/MenuContext';
import Layout from '@components/layout/Layout';
import { DefaultSeo } from '@components/SEO';
import WhatsAppFab from '@components/WhatsAppFab';
import useAnimationPreferences from '@hooks/use-animation-preferences';
import { useLocale } from '@hooks/use-locale';
import { ThemeProvider } from '@hooks/use-theme';
import { reportUnhandledUiErrorToSentry } from '@lib/error-reporting';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';

type LenisCtor = (typeof import('lenis'))['default'];
type LenisInstance = InstanceType<LenisCtor>;

export const Route = createRootRoute({
  head: () => ({
    links: [{ rel: 'stylesheet', href: appCss }],
  }),
  component: RootRouteComponent,
  notFoundComponent: NotFoundBoundary,
});

function RootRouteComponent() {
  const { shouldAnimate } = useAnimationPreferences();
  const { locale, metadata } = useLocale();
  const isTamil = locale === 'ta';
  useSmoothScrolling(shouldAnimate);

  return (
    <RootDocument locale={metadata.hrefLang} direction={metadata.dir}>
      <a href="#main-content" className="skip-to-content">
        {isTamil ? 'முதன்மை உள்ளடக்கத்துக்கு செல்லவும்' : 'Skip to main content'}
      </a>
      <AppErrorBoundary fallback={<RootErrorFallback isTamil={isTamil} />}>
        <ThemeProvider>
          <MenuProvider>
            <AnimatedLayout>
              <DefaultSeo />
              <Toaster
                position="top-center"
                toastOptions={{
                  duration: 5000,
                  style: {
                    background: 'var(--card)',
                    color: 'var(--card-foreground)',
                    border: '1px solid var(--border)',
                  },
                }}
              />
              <Layout>
                <Outlet />
              </Layout>
              <WhatsAppFab phoneNumber="+918870435979" />
              {import.meta.env.DEV ? <TanStackRouterDevtools position="bottom-right" /> : null}
            </AnimatedLayout>
          </MenuProvider>
        </ThemeProvider>
      </AppErrorBoundary>
    </RootDocument>
  );
}

type AppErrorBoundaryProps = {
  children: ReactNode;
  fallback: ReactNode;
};

type AppErrorBoundaryState = {
  hasError: boolean;
};

class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    reportUnhandledUiErrorToSentry(error, {
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

function RootErrorFallback({ isTamil }: { isTamil: boolean }) {
  return (
    <main className="bg-background flex min-h-screen items-center justify-center px-4 py-20">
      <section className="max-w-xl px-6 py-14 text-center">
        <p className="font-heading text-foreground text-3xl">
          {isTamil ? 'இந்த பக்கத்தை தற்போது காட்ட முடியவில்லை.' : 'We cannot show this page right now.'}
        </p>
        <p className="text-muted-foreground mt-3 text-sm leading-7">
          {isTamil ? 'சில விநாடிகளில் பக்கத்தை மீண்டும் ஏற்றவும்.' : 'Please refresh the page in a few seconds.'}
        </p>
      </section>
    </main>
  );
}

function RootDocument({
  children,
  locale,
  direction,
}: Readonly<{
  children: ReactNode;
  locale: string;
  direction: 'ltr' | 'rtl';
}>) {
  return (
    <HelmetProvider>
      <html lang={locale} dir={direction} suppressHydrationWarning>
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" type="image/png" href="/favicon/favicon-96x96.png" sizes="96x96" />
          <link rel="icon" type="image/svg+xml" href="/favicon/favicon.svg" />
          <link rel="shortcut icon" href="/favicon/favicon.ico" />
          <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
          <link rel="manifest" href="/favicon/site.webmanifest" />
          {import.meta.env.PROD ? (
            <>
              <script async src="https://www.googletagmanager.com/gtag/js?id=GTM-NTDD2T98" />
              <script
                dangerouslySetInnerHTML={{
                  __html: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'GTM-NTDD2T98');`,
                }}
              />
            </>
          ) : null}
          <HeadContent />
        </head>
        <body>
          {children}
          <Scripts />
        </body>
      </html>
    </HelmetProvider>
  );
}

function NotFoundBoundary() {
  return <NotFoundPage />;
}

function useSmoothScrolling(shouldAnimate: boolean) {
  useEffect(() => {
    if (!shouldAnimate) {
      return;
    }

    const canUseEnhancedScrolling = window.matchMedia('(pointer: fine) and (min-width: 768px)').matches;
    if (!canUseEnhancedScrolling) {
      return;
    }

    let lenis: LenisInstance | null = null;
    let rafId = 0;
    let cancelled = false;

    const setupLenis = async () => {
      const { default: Lenis } = await import('lenis');
      if (cancelled) {
        return;
      }

      lenis = new Lenis({
        duration: 0.95,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 2,
      });

      const raf = (time: number) => {
        lenis?.raf(time);
        rafId = requestAnimationFrame(raf);
      };

      rafId = requestAnimationFrame(raf);
    };

    setupLenis().catch((error) => {
      if (import.meta.env.DEV) {
        console.warn('Failed to load Lenis for smooth scrolling', error);
      }
    });

    return () => {
      cancelled = true;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      lenis?.destroy();
    };
  }, [shouldAnimate]);
}
